import { Array, Boolean, Guard, Number, Record, Static, String } from 'runtypes';

import { ActivityLinkButtonRunType } from 'src/models/activityLink';
import { ArchivButtonRunType } from 'src/models/archiv';
import { GlobalizationButtonRunType } from 'src/models/globalization';
import { IdoButtonRunType } from 'src/models/ido';
import { type ModuleLayout as _ModuleLayout, MODULE_LAYOUT_VALUES } from 'src/models/note/enums';
import { SpoingButtonRunType } from 'src/models/spoing';
import { StatusButtonRunType } from 'src/models/status';
import { TaskButtonRunType } from 'src/models/task';

const ModuleLayoutRunType = Guard(
	(value: unknown): value is _ModuleLayout =>
		typeof value === 'string' && MODULE_LAYOUT_VALUES.includes(value as _ModuleLayout)
);

const NoteLockConfigRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	date: String,
	locked: Boolean,
	processId: String,
	userDisplayName: String,
	userId: Number,
	userShortcut: String,
});

const NoteAlarmConfigNotifiableUserRunType = Record({
	date: String,
	notified: Boolean,
	userDisplayname: String,
});

const NoteAlarmConfigRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	alarmConfigNotifiableUsers: Array(NoteAlarmConfigNotifiableUserRunType),
	date: String,
	notifiable: Boolean,
	ownerDate: String,
	ownerNotified: Boolean,
	ownerType: String,
	processId: String,
});

const NoteRepetitionConfigRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	countEnd: Number,
	cycleTime: Number,
	end: String,
	fixedEndDate: String,
	monthlyRule: String,
	processId: String,
	type: String,
	weekDaySelectorFriday: Boolean,
	weekDaySelectorMonday: Boolean,
	weekDaySelectorSaturday: Boolean,
	weekDaySelectorSunday: Boolean,
	weekDaySelectorThursday: Boolean,
	weekDaySelectorTuesday: Boolean,
	weekDaySelectorWednesday: Boolean,
});

const NoteFavoriteButtonRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	changeable: Boolean,
	favored: Boolean,
	processId: String,
	render: Boolean,
});

const NoteHistoryFragmentRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	date: String,
	key: String,
	processId: String,
	userShortcut: String,
	value: String,
});

const NoteHistoryEntriesRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	historyFragments: Array(NoteHistoryFragmentRunType),
	processId: String,
	userShortcut: String,
});

export const NoteAvailableUsersRunType = Record({
	Displayname: String,
	ID: Number,
	UserAvatar: String,
	UserDisplayname: String,
});

export type NoteAvailableUsers = Static<typeof NoteAvailableUsersRunType>;

const NoteAllUsersRunType = Record({
	Displayname: String,
	ID: Number,
	UserAvatar: String,
	UserDisplayname: String,
});

export const NoteRunType = Record({
	activitylinkButton: ActivityLinkButtonRunType,
	aggregateId: String,
	aggregateVersion: Number,
	alarmConfig: NoteAlarmConfigRunType,
	allUsers: Array(NoteAllUsersRunType),
	archivButton: ArchivButtonRunType,
	availableUsers: Array(NoteAvailableUsersRunType),
	changeable: Boolean,
	content: String,
	favoriteButton: NoteFavoriteButtonRunType,
	fixedMaturityDate: String,
	forerunTime: Number,
	globalizationButton: GlobalizationButtonRunType,
	historyEntries: Array(NoteHistoryEntriesRunType),
	idoButton: IdoButtonRunType,
	inactive: Boolean,
	lockConfig: NoteLockConfigRunType,
	moduleLayout: ModuleLayoutRunType,
	ownerUserId: Number,
	processId: String,
	repetitionConfig: NoteRepetitionConfigRunType,
	responsibleUserId: Number,
	spoingButton: SpoingButtonRunType,
	statusButton: StatusButtonRunType,
	taskButton: TaskButtonRunType,
	title: String,
});

export type Note = Static<typeof NoteRunType>;

export const NoteFlatHistoryEntryRunType = Record({
	author: String,
	date: String,
	title: String,
});

export type NoteFlatHistoryEntry = Static<typeof NoteFlatHistoryEntryRunType>;
